import React, { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  X,
  Crop as CropIcon,
  RotateCcw,
  Check,
  Loader2,
  RotateCw,
  FlipHorizontal2,
  FlipVertical2,
} from "lucide-react";

const ASPECT_PRESETS = [
  { key: "free", label: "Bebas", value: undefined },
  { key: "1:1", label: "1:1", value: 1 },
  { key: "4:5", label: "4:5", value: 4 / 5 },
  { key: "4:3", label: "4:3", value: 4 / 3 },
  { key: "16:9", label: "16:9", value: 16 / 9 },
];

const centerInitialCrop = (aspect, imgWidth, imgHeight) => {
  const a = aspect ?? imgWidth / imgHeight;
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, a, imgWidth, imgHeight),
    imgWidth,
    imgHeight,
  );
};

/**
 * Renders `originalDataUrl` onto a canvas with the given rotation (deg, multiples of 90)
 * and flip flags baked in. Returns a data URL of the transformed image.
 * Rotating from source (not accumulating) avoids quality loss from repeated re-encoding.
 */
const renderTransformed = (originalDataUrl, rotation, flipX, flipY) =>
  new Promise((resolve, reject) => {
    if (!originalDataUrl) return resolve("");
    if (rotation === 0 && !flipX && !flipY) return resolve(originalDataUrl);
    const img = new Image();
    img.onload = () => {
      const rot = ((rotation % 360) + 360) % 360;
      const swap = rot === 90 || rot === 270;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = swap ? h : w;
      canvas.height = swap ? w : h;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rot * Math.PI) / 180);
      ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
      ctx.drawImage(img, -w / 2, -h / 2);
      // Preserve PNG for transparency, else JPEG for size
      const isPng = originalDataUrl.startsWith("data:image/png");
      const out = canvas.toDataURL(isPng ? "image/png" : "image/jpeg", 0.92);
      resolve(out);
    };
    img.onerror = reject;
    img.src = originalDataUrl;
  });

/**
 * Modal that lets user crop / rotate / flip an image before it's uploaded.
 * Props:
 *   file           — File object (from <input type=file>)
 *   defaultAspect  — one of ASPECT_PRESETS.key, default "free"
 *   onCancel       — user closed without applying
 *   onCropped(fileBlob, filename) — user applied; blob is the final image
 */
const CropModal = ({ file, defaultAspect = "free", onCancel, onCropped }) => {
  const [originalSrc, setOriginalSrc] = useState(""); // untouched source
  const [imgSrc, setImgSrc] = useState("");           // transformed for display
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [aspectKey, setAspectKey] = useState(defaultAspect);
  const [processing, setProcessing] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);
  const imgRef = useRef(null);

  const aspect = ASPECT_PRESETS.find((p) => p.key === aspectKey)?.value;

  React.useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result?.toString() || "";
      setOriginalSrc(src);
      setImgSrc(src);
      setRotation(0);
      setFlipX(false);
      setFlipY(false);
    };
    reader.readAsDataURL(file);
  }, [file]);

  // Regenerate the display image whenever a transform changes.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!originalSrc) return;
      const next = await renderTransformed(originalSrc, rotation, flipX, flipY);
      if (!cancelled) {
        setImgSrc(next);
        setCrop(undefined);           // let onImageLoad re-center
        setCompletedCrop(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [originalSrc, rotation, flipX, flipY]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerInitialCrop(aspect, width, height));
  };

  const changeAspect = (key) => {
    setAspectKey(key);
    const next = ASPECT_PRESETS.find((p) => p.key === key)?.value;
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(centerInitialCrop(next, width, height));
    }
  };

  const resetCrop = () => {
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(centerInitialCrop(aspect, width, height));
    }
  };

  const applyCrop = async () => {
    const transformed = rotation !== 0 || flipX || flipY;
    if (!imgRef.current || !completedCrop) {
      // User didn't drag — send the current (possibly transformed) image at natural resolution
      if (transformed) {
        await sendCurrentAsFile();
      } else {
        onCropped(file, file.name);
      }
      return;
    }
    setProcessing(true);
    try {
      const img = imgRef.current;
      // Convert percent/px crop → source-pixel crop
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const sx = completedCrop.x * scaleX;
      const sy = completedCrop.y * scaleY;
      const sw = completedCrop.width * scaleX;
      const sh = completedCrop.height * scaleY;

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

      // Preserve type when possible; PNG for transparency, else JPEG for size.
      const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
      const quality = outType === "image/jpeg" ? 0.9 : undefined;
      const blob = await new Promise((res) => canvas.toBlob(res, outType, quality));
      if (!blob) throw new Error("canvas.toBlob failed");

      // Derive filename with .jpg or .png
      const base = file.name.replace(/\.[^.]+$/, "");
      const ext = outType === "image/png" ? ".png" : ".jpg";
      const outFile = new File([blob], `${base}${ext}`, { type: outType });
      onCropped(outFile, outFile.name);
    } finally {
      setProcessing(false);
    }
  };

  // Upload the current display image (with rotation/flip baked in) at full resolution.
  const sendCurrentAsFile = async () => {
    if (!imgRef.current) return onCropped(file, file.name);
    setProcessing(true);
    try {
      const img = imgRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0);
      const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
      const quality = outType === "image/jpeg" ? 0.92 : undefined;
      const blob = await new Promise((res) => canvas.toBlob(res, outType, quality));
      if (!blob) throw new Error("canvas.toBlob failed");
      const base = file.name.replace(/\.[^.]+$/, "");
      const ext = outType === "image/png" ? ".png" : ".jpg";
      const outFile = new File([blob], `${base}${ext}`, { type: outType });
      onCropped(outFile, outFile.name);
    } finally {
      setProcessing(false);
    }
  };

  const skipCrop = async () => {
    if (rotation !== 0 || flipX || flipY) {
      await sendCurrentAsFile();
    } else {
      onCropped(file, file.name);
    }
  };

  const rotateCw = () => setRotation((r) => (r + 90) % 360);
  const rotateCcw = () => setRotation((r) => (r + 270) % 360);
  const toggleFlipX = () => setFlipX((v) => !v);
  const toggleFlipY = () => setFlipY((v) => !v);
  const resetTransform = () => {
    setRotation(0);
    setFlipX(false);
    setFlipY(false);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="image-crop-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CropIcon className="h-5 w-5 text-blue-800" />
            <div>
              <h3 className="font-heading text-lg font-bold text-slate-900">Crop Gambar</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Drag pojok kotak untuk atur area, atau pilih rasio di bawah.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto bg-slate-50">
          {imgSrc ? (
            <div className="flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={(_, pc) => setCrop(pc)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={imgSrc}
                  alt="untuk di-crop"
                  onLoad={onImageLoad}
                  data-testid="image-crop-source"
                  style={{ maxHeight: "60vh", maxWidth: "100%" }}
                />
              </ReactCrop>
            </div>
          ) : (
            <div className="p-12 flex items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </div>

        <div className="p-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">
            Transform:
          </span>
          <button
            type="button"
            onClick={rotateCcw}
            data-testid="image-crop-rotate-ccw"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-800 text-slate-600"
            title="Putar 90° ke kiri"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            90° ←
          </button>
          <button
            type="button"
            onClick={rotateCw}
            data-testid="image-crop-rotate-cw"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-800 text-slate-600"
            title="Putar 90° ke kanan"
          >
            <RotateCw className="h-3.5 w-3.5" />
            90° →
          </button>
          <button
            type="button"
            onClick={toggleFlipX}
            data-testid="image-crop-flip-x"
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border ${
              flipX
                ? "bg-blue-800 text-white border-blue-800"
                : "bg-white border-slate-200 hover:border-blue-300 hover:text-blue-800 text-slate-600"
            }`}
            title="Balik horizontal (mirror)"
          >
            <FlipHorizontal2 className="h-3.5 w-3.5" />
            Flip H
          </button>
          <button
            type="button"
            onClick={toggleFlipY}
            data-testid="image-crop-flip-y"
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border ${
              flipY
                ? "bg-blue-800 text-white border-blue-800"
                : "bg-white border-slate-200 hover:border-blue-300 hover:text-blue-800 text-slate-600"
            }`}
            title="Balik vertikal"
          >
            <FlipVertical2 className="h-3.5 w-3.5" />
            Flip V
          </button>
          {(rotation !== 0 || flipX || flipY) && (
            <button
              type="button"
              onClick={resetTransform}
              data-testid="image-crop-transform-reset"
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 px-2 py-1.5"
              title="Kembalikan orientasi asli"
            >
              <X className="h-3.5 w-3.5" />
              Reset Orientasi
            </button>
          )}
          <span className="ml-auto text-[10px] font-mono text-slate-400">
            {rotation}°{flipX ? " · Hflip" : ""}{flipY ? " · Vflip" : ""}
          </span>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">
            Rasio:
          </span>
          {ASPECT_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => changeAspect(p.key)}
              data-testid={`image-crop-aspect-${p.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                aspectKey === p.key
                  ? "bg-blue-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={resetCrop}
            data-testid="image-crop-reset"
            className="ml-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-800 px-2 py-1.5"
            title="Reset seleksi"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={skipCrop}
              data-testid="image-crop-skip"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2"
            >
              Lewati Crop
            </button>
            <button
              type="button"
              onClick={applyCrop}
              disabled={processing || !imgSrc}
              data-testid="image-crop-apply"
              className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2 rounded-full shadow-md shadow-blue-900/20"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Terapkan Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropModal;
